import { GlobalTypes } from '@modules';
import { CurrencyEnum, FiatCurrencyEnum, PhoneFormatsType } from '@modules/global/types';
import { TransactionType } from '@modules/transactions/types';
import { VerifyNextEnum } from '@modules/types';

import type { AccountsOperationsEnum } from './state';

export enum PaymentAliasEnum {
  iban_exchange = 'iban_exchange',
  iban_invoice = 'iban_invoice',
  iban_withdraw = 'iban_withdraw',
}

export enum TransactionTypesEnum {
  fiat_buy = 'fiat_buy',
  fiat_sell = 'fiat_sell',
  fiat_withdraw = 'fiat_withdraw',
  fiat_deposit = 'fiat_deposit',
}

export enum AccountCreateStatus {
  success = 'success',
  fail = 'fail',
}

// accounts/sell

export type AccountsSellParams = {
  sell_token: string;
};

export type AccountsSellResponse = {
  id: string;
  status: string;
};

// accounts

export type AccountsResponse = {
  iban: string;
  first_name_en: string;
  last_name_en: string;
  country_code: string;
  swift: string;
  created_at: string;
  created_at_ts: number;
  updated_at: string;
  updated_at_ts: number;
  beneficiary_country: string;
  beneficiary_state: string;
  beneficiary_city: string;
  beneficiary_address_line: string;
  beneficiary_postcode: string;
  beneficiary_bank_name: string;
  beneficiary_bank_address: string;
  beneficiary_bank_city: string;
  beneficiary_bank_country: string;
  fiat_balance: string;
};

// accounts/intention

export type AccountIntention = {
  status: string;
  data: unknown[];
};

export type AccountDetails = Omit<AccountsResponse, 'fiat_balance'>;

// accounts/limits

export type AccountsLimitsParams = {
  from: GlobalTypes.CurrencyEnum;
  to: GlobalTypes.CurrencyEnum;
  type: AccountsOperationsEnum;
  payment_method: PaymentAliasEnum;
};

export type AccountsLimitsResponse = {
  currencies_limits: {
    [key in CurrencyEnum]: {
      limits: {
        day: string;
        day_init: string;
        month: string;
        month_init: string;
      };
    };
  };
  transactions_limits: {
    [key in CurrencyEnum]: {
      min: string;
      max: string;
    };
  };
};

// accounts/sell-rate

export type AccountsSellRateParams = {
  from: CurrencyEnum;
  to: CurrencyEnum;
  amount: string;
  payment_alias: PaymentAliasEnum;
};

export type AccountsSellRateResponse = {
  sell_token: string;
  rate: string;
  rate_with_fee: string;
  fee: {
    [currency in CurrencyEnum]: string;
  };
  subtotal: {
    [currency in CurrencyEnum]: string;
  };
  total: {
    [currency in CurrencyEnum]: string;
  };
};

// accounts/recent-transaction-accounts

export type RecentAccountTransaction = {
  iban: string;
  full_name: string;
  swift: string;
  country: string;
  created_at: string;
};

export type AccountsRecentTransactionsResponse = RecentAccountTransaction[];

// accounts/withdraw

export type AccountsWithdrawParams = {
  sender_iban: string;
  full_name: string;
  swift: string;
  country_code: PhoneFormatsType;
  currency: string;
  purpose: string;
  captcha?: string;
  amount: string;
  beneficiary_iban: string;
};

export type AccountsWithdrawResponse = {
  key: string;
  code_length: number;
  next: VerifyNextEnum;
  timeout: number;
};

// accounts/withdraw/verify

export enum AccountsWithdrawStatusesEnum {
  succeeded = 'succeeded',
  failed = 'failed',
  hold = 'hold',
}

export type AccountsWithdrawVerifyParams = {
  key: string;
  code: string;
  type: 'phone' | 'email';
};

export type AccountsWithdrawVerifyResponse = {
  id: string;
  status: AccountsWithdrawStatusesEnum;
};

// accounts/withdraw/verify/resend

export type AccountsWithdrawVerifyResendParams = {
  key: string;
  captcha?: string;
};

export type AccountsWithdrawVerifyResendResponse = {
  key: string;
  code_length: number;
  timeout: number;
};

// accounts/buy

export type AccountsBuyParams = {
  buy_token: string;
};

export type TAccountsBuyAddress = {
  country: string;
  city: string;
  address_line: string;
};

export type AccountsBuyResponse = {
  instructions: {
    iban: string;
    swift: string;
    full_name: string;
    bank_name: string;
    address: TAccountsBuyAddress;
    bank_address: TAccountsBuyAddress;
  };
  id: string;
  state: string;
};

// accounts/estimate-fee

export type AccountsEstimateFeeParams = {
  iban: string;
  currency: FiatCurrencyEnum;
  amount: string;
};

export type AccountsEstimateFeeResponse = {
  currency: FiatCurrencyEnum;
  subtotal: string;
  fee: string;
  total: string;
};

// accounts/buy-rate

export type AccountsBuyRateParams = {
  from: CurrencyEnum;
  to: CurrencyEnum;
  amount: string;
  payment_alias: PaymentAliasEnum;
};

export type AccountsBuyRateResponse = {
  buy_token: string;
  rate: string;
  rate_with_fee: string;
  fee: {
    [currency in CurrencyEnum]: string;
  };
  subtotal: {
    [currency in CurrencyEnum]: string;
  };
  total: {
    [currency in CurrencyEnum]: string;
  };
};
